/**
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-17 12:10:24
 */

import ApiClient from '~/client/ApiClient';

// types
import type {HandleType} from '~/helpers/shopify';
import type {ProductType} from '~/constants/products';
import type {SuccessResponse} from '~/types/api-client.types';
import type {
  DateOption, ICustomField, IGeneralSettings,
  IProductDetail, ITemplate
} from '~/types/api.types';

/**
 * @public
 * @static
 * Returns the product types that match the given handle type
 * @param handle - The handle type
 * @return The product types
 */
const getProductTypesByHandleType = (handle: HandleType): ProductType[] => {
  switch (handle) {
    case 'send-postcard':
      return ['Postcards'];
    case 'send-snap-pack-mailer':
      return ['Snap Pack Mailers'];
    case 'send-self-mailer':
      return ['Bi-Fold Self-Mailers', 'Tri-Fold Self-Mailers'];
    case 'send-personal-letter':
      return ['Personal Letters'];
    case 'send-professional-letter':
      return ['Professional Letters'];
    case 'send-real-penned-letter':
      return ['Real Penned Letter'];
    default:
      throw new Error('Unknown handle type given');
  }
};

/**
 * @public
 * @static
 * Fetches the templates for the given handle type
 * @param apiClient - The API client instance
 * @param handle - The handle type
 * @returns The fetch templates list
 */
export const fetchTemplatesApi = async (
  apiClient: InstanceType<typeof ApiClient>, handle: HandleType,
): Promise<ITemplate[]> => {
  try {
    const response = await apiClient
      .postWithHandle<SuccessResponse<ITemplate[]>, {
        productType: ProductType | ProductType[]
      }>('/api/templates', handle, {
        productType: getProductTypesByHandleType(handle),
      });
    return response?.data?.data ?? [];
  } catch (e) {
    console.log(e);
    throw e;
  }
};

/**
 * @public
 * @static
 * Fetches the select dates based on the subscription plan
 * @param apiClient - The API client instance
 * @returns The select dates list
 */
export const fetchSelectDates = async (
  apiClient: InstanceType<typeof ApiClient>,
): Promise<DateOption[]> => {
  try {
    const response = await apiClient
      .post<SuccessResponse<DateOption[]>>(
        '/account/select-dates');
    return response?.data?.data ?? [];
  } catch (e) {
    console.log(e);
    throw e;
  }
};

/**
 * @public
 * @static
 * Returns the product details for the given handle type
 * @param apiClient - The API client instance
 * @param handle - The handle type
 * @returns The product details or list of products detail
 */
export const fetchProductDetails = async (
  apiClient: InstanceType<typeof ApiClient>, handle: HandleType,
): Promise<IProductDetail | IProductDetail[]> => {
  try {
    const response = await apiClient
      .post<SuccessResponse<IProductDetail | IProductDetail[]>>(
        '/api/product-details', {
          type: getProductTypesByHandleType(handle),
        });
    return response?.data?.data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

/**
 * @public
 * @static
 * Returns the account specific custom fields
 * @param apiClient - The API client instance
 * @returns The custom fields list
 */
export const fetchCustomFields = async (
  apiClient: InstanceType<typeof ApiClient>,
): Promise<ICustomField[]> => {
  try {
    const response = await apiClient
      .post<SuccessResponse<ICustomField[]>>(
        '/account/custom-fields');
    return response?.data?.data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

/**
 * @public
 * @static
 * Fetches the user account general configuration
 * @param apiClient - The API client instance
 * @returns The general settings
 */
export const fetchGeneralSettings = async (
  apiClient: InstanceType<typeof ApiClient>,
): Promise<IGeneralSettings> => {
  try {
    const response = await apiClient
      .post<SuccessResponse<IGeneralSettings>>(
        '/account/general-settings');
    return response?.data?.data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};
